/**
 * PreferenciasPresets — 6 widget pra pagina /notificacoes/preferencias.
 *
 * Spec: roadmap notificacoes §1 ultrathink (linha 880) — 4 quick presets
 * que aplicam configuracao inteira de uma vez via batch PATCH em
 * `/me/notification-subscriptions`.
 *
 * Comportamento:
 * - 3 botoes concretos (DevOps Pro / Gerente / Conservador) + 1 sentinela
 *   "Custom" (no-op; mostra toast informativo).
 * - Click em preset concreto abre confirm modal Mantine (substituicao destrutiva).
 * - Smart-default callout: se o user tem `applied_template_name` mapeado em
 *   TEMPLATE_TO_PRESET e ainda nao dismissou, mostra Callout no topo do card
 *   "Recomendado pra voce: {preset.name}" + link "Aplicar".
 * - Callout dismissable via localStorage flag `notif_presets_callout_dismissed`.
 */
import { Group, Stack, Text, ThemeIcon, Button, Badge, Anchor } from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import {
  IconAdjustments,
  IconBolt,
  IconShieldLock,
  IconSparkles,
  IconUserCog,
  IconX,
} from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import {
  invalidateMeNotificationQueries,
  notificationPreferencesApi,
  type Subscription,
} from '@/api/notification-preferences'
import { formatApiError } from '@/api/errors'
import { Callout, WidgetCard } from '@/components/ui'
import { useAuthStore } from '@/store/auth'
import {
  CALLOUT_DISMISSED_KEY,
  PRESETS,
  PRESET_LABELS,
  PRESET_NAMES,
  TEMPLATE_TO_PRESET,
  presetItemsToUpdates,
  type PresetItem,
  type PresetName,
} from './presets'

const QUERY_KEY = ['me', 'notification-subscriptions'] as const

const PRESET_ICON_MAP = {
  IconBolt,
  IconUserCog,
  IconShieldLock,
  IconAdjustments,
}

function getCalloutDismissed(): boolean {
  if (typeof window === 'undefined') return true
  return localStorage.getItem(CALLOUT_DISMISSED_KEY) === '1'
}

function setCalloutDismissed() {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(CALLOUT_DISMISSED_KEY, '1')
  } catch {
    /* localStorage indisponivel — ignora */
  }
}

/**
 * Detecta qual preset (devops_pro/gerente/conservador) está aplicado
 * comparando cada PresetItem com a row correspondente em `subs`.
 *
 * Match = todos os items do preset existem em subs com mesmo
 * enabled+min_severity. Rows extras em subs (sources/channels fora do
 * preset) sao ignoradas — apenas customizacoes orthogonais.
 *
 * Se multiplos presets matcharem, retorna o com MAIS items (mais especifico
 * — Conservador tem 68 items vs Gerente ~20).
 *
 * Retorna 'custom' se nenhum match — usuario divergiu manualmente.
 */
function detectActivePreset(subs: Subscription[] | undefined): PresetName {
  if (!subs || subs.length === 0) return 'custom'
  const key = (s: { source: string; channel: string }) => `${s.source}|${s.channel}`
  const subMap = new Map<string, Subscription>()
  for (const s of subs) subMap.set(key(s), s)

  const matches = (items: PresetItem[]): boolean => {
    for (const it of items) {
      const found = subMap.get(key(it))
      if (!found) return false
      if (found.enabled !== it.enabled) return false
      if (found.min_severity !== it.min_severity) return false
    }
    return true
  }

  let active: PresetName = 'custom'
  let bestSize = -1
  for (const name of ['devops_pro', 'gerente', 'conservador'] as const) {
    const items = PRESETS[name]
    if (matches(items) && items.length > bestSize) {
      active = name
      bestSize = items.length
    }
  }
  return active
}

export function PreferenciasPresets() {
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const [calloutDismissed, setLocalCalloutDismissed] = useState<boolean>(
    () => getCalloutDismissed(),
  )

  const { data: subsData } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => notificationPreferencesApi.listSubscriptions(),
  })

  // Preset atualmente aplicado, derivado do estado em DB.
  const activePreset = useMemo(
    () => detectActivePreset(subsData?.items),
    [subsData?.items],
  )

  // Preset recomendado via persona (applied_template_name → PresetName).
  const recommended: PresetName | null = useMemo(() => {
    const tplName = user?.applied_template_name
    if (!tplName) return null
    return TEMPLATE_TO_PRESET[tplName] ?? null
  }, [user?.applied_template_name])

  const applyMutation = useMutation({
    mutationFn: (name: Exclude<PresetName, 'custom'>) => {
      const updates = presetItemsToUpdates(PRESETS[name])
      return notificationPreferencesApi.updateSubscriptions(updates)
    },
    onSuccess: (_res, name) => {
      invalidateMeNotificationQueries(qc)
      notifications.show({
        color: 'green',
        title: 'Preset aplicado',
        message: `${PRESETS[name].length} subscriptions atualizadas (${PRESET_LABELS[name].name}).`,
      })
    },
    onError: (err: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Erro ao aplicar preset',
        message: formatApiError(err, 'Falha ao aplicar preset.'),
      })
    },
  })

  const applyPreset = (name: PresetName) => {
    if (name === 'custom') {
      notifications.show({
        color: 'blue',
        title: 'Custom',
        message: 'Configure manualmente nos cards abaixo.',
      })
      return
    }
    const label = PRESET_LABELS[name]
    modals.openConfirmModal({
      title: `Aplicar preset "${label.name}"?`,
      children: (
        <Stack gap="xs">
          <Text size="sm">
            Substituira suas configurações atuais de canais, severidades e
            filtros por fonte. Esta ação não pode ser desfeita automaticamente
            — você precisara reconfigurar manualmente caso queira voltar.
          </Text>
          <Text size="xs" c="dimmed">
            {label.description}
          </Text>
        </Stack>
      ),
      confirmProps: { color: 'blue' },
      labels: { confirm: 'Aplicar', cancel: 'Cancelar' },
      onConfirm: () => {
        applyMutation.mutate(name)
      },
    })
  }

  const dismissCallout = () => {
    setCalloutDismissed()
    setLocalCalloutDismissed(true)
  }

  return (
    <WidgetCard p="md">
      <Stack gap="sm">
        <Group gap={8} align="center">
          <ThemeIcon size={32} radius="md" color="grape" variant="light">
            <IconSparkles size={18} />
          </ThemeIcon>
          <Text fw={600} size="sm">Quick presets</Text>
        </Group>

        <Text size="xs" c="dimmed">
          Aplique uma configuração completa em um clique. Cada preset substitui
          canais, severidades e filtros por fonte. Use "Custom" se já
          personalizou manualmente.
        </Text>

        {recommended && !calloutDismissed && (
          <Callout variant="info">
            <Group justify="space-between" align="center" wrap="nowrap">
              <Text size="xs">
                Recomendado para você:{' '}
                <strong>{PRESET_LABELS[recommended].name}</strong>{' '}
                <Text component="span" c="dimmed" size="xs">
                  ({PRESET_LABELS[recommended].description})
                </Text>{' '}
                —{' '}
                <Anchor
                  size="xs"
                  component="button"
                  type="button"
                  onClick={() => applyPreset(recommended)}
                >
                  Aplicar
                </Anchor>
              </Text>
              <Button
                size="compact-xs"
                variant="subtle"
                color="gray"
                leftSection={<IconX size={12} />}
                onClick={dismissCallout}
                aria-label="Dismiss recommendation"
              >
                Dispensar
              </Button>
            </Group>
          </Callout>
        )}

        <Group gap="sm" wrap="wrap">
          {PRESET_NAMES.map((name) => {
            const label = PRESET_LABELS[name]
            const Icon = PRESET_ICON_MAP[label.icon]
            const isRecommended = recommended === name
            const isCustom = name === 'custom'
            const isActive = activePreset === name
            return (
              <Stack
                key={name}
                gap={4}
                style={{
                  flex: '1 1 200px',
                  minWidth: 180,
                  borderRadius: 8,
                  border: isActive
                    ? '2px solid var(--mantine-color-green-6)'
                    : '2px solid transparent',
                  padding: isActive ? 4 : 6,
                  transition: 'border-color 0.15s ease',
                }}
              >
                <Button
                  variant={isActive ? 'filled' : isRecommended ? 'filled' : 'light'}
                  color={isActive ? 'green' : label.color}
                  leftSection={<Icon size={16} />}
                  onClick={() => applyPreset(name)}
                  loading={
                    applyMutation.isPending &&
                    applyMutation.variables === name &&
                    !isCustom
                  }
                  disabled={applyMutation.isPending}
                  fullWidth
                  justify="flex-start"
                >
                  <Group gap={6} wrap="nowrap">
                    <Text size="sm" fw={600}>{label.name}</Text>
                    {isActive && (
                      <Badge size="xs" variant="filled" color="green">
                        Ativo
                      </Badge>
                    )}
                    {isCustom && !isActive && (
                      <Badge size="xs" variant="light" color="gray">
                        Já personalizado
                      </Badge>
                    )}
                    {isRecommended && !isActive && (
                      <Badge size="xs" variant="filled" color={label.color}>
                        Recomendado
                      </Badge>
                    )}
                  </Group>
                </Button>
                <Text size="xs" c="dimmed" px={4}>
                  {label.description}
                </Text>
              </Stack>
            )
          })}
        </Group>
      </Stack>
    </WidgetCard>
  )
}
